// アバタータブ: .fmavatar の読み込み（ファイル選択 / D&D）、保存済み一覧、統計表示。

import { describeError } from "../core/errors.ts";
import { store } from "../store/state.ts";
import { confirmDialog, el, icon, openMenu } from "./dom.ts";
import { openReceiveDialog, relaySettings } from "./transferView.ts";

export async function handleFile(file: File): Promise<void> {
  store.setBanner(null);
  try {
    const buf = new Uint8Array(await file.arrayBuffer());
    const dup = await store.findDuplicateByHash(buf);
    if (dup) {
      const savedAt = new Date(dup.savedAt).toLocaleString("ja-JP");
      const ok = await confirmDialog(
        "同じファイルを読み込み済みです",
        `「${dup.name}」（保存日時: ${savedAt}）と同じ内容の .fmavatar です。上書きしますか？`,
        "上書きする",
      );
      if (!ok) return;
      await store.loadFromBytes(buf, true, dup.id);
    } else {
      await store.loadFromBytes(buf, true);
    }
    const name = store.avatar?.manifest.avatarName ?? "?";
    const saved = store.savedAvatars.find((s) => s.id === store.avatar?.id);
    const savedAt = saved ? new Date(saved.savedAt).toLocaleString("ja-JP") : null;
    const title = `「${name}」を読み込みました${savedAt ? `（保存日時: ${savedAt}）` : ""}`;
    const warnings = store.avatar?.warnings ?? [];
    if (warnings.length > 0) {
      store.setBanner({
        kind: "info",
        title: `${title} — 注意 ${warnings.length} 件`,
        detail: warnings.join("\n"),
        hint: null,
      });
    } else {
      store.setTab("expression");
      store.setBanner({ kind: "info", title, detail: "", hint: null });
    }
  } catch (e) {
    const d = describeError(e);
    store.setBanner({ kind: "error", title: d.title, detail: d.detail, hint: d.hint });
  }
}

function dropzone(): HTMLElement {
  const input = el("input", {
    type: "file",
    accept: ".fmavatar,application/zip",
    style: { display: "none" },
    onchange: (e: Event) => {
      const f = (e.target as HTMLInputElement).files?.[0];
      if (f) void handleFile(f);
      (e.target as HTMLInputElement).value = "";
    },
  });

  const zone = el(
    "div",
    {
      class: "dropzone",
      onclick: () => input.click(),
      ondragover: (e: DragEvent) => {
        e.preventDefault();
        zone.classList.add("over");
      },
      ondragleave: () => zone.classList.remove("over"),
      ondrop: (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        zone.classList.remove("over");
        const f = e.dataTransfer?.files?.[0];
        if (f) void handleFile(f);
      },
    },
    el("div", { class: "big" }, icon("folder")),
    el("div", { text: ".fmavatar ファイルを選択" }),
    el("div", { class: "muted", text: "ここにドラッグ＆ドロップもできます" }),
  );

  return el("div", {}, zone, input);
}

function currentAvatarCard(): HTMLElement | null {
  const a = store.avatar;
  if (!a) return null;
  const totalShapes = a.face.meshes.reduce((n, m) => n + m.blendshapeNames.length, 0);
  const totalVerts = a.meshes.blocks.reduce((n, b) => n + b.vertexCount, 0);

  const dl = el("dl", { class: "stat-grid" });
  const add = (k: string, v: string): void => {
    dl.appendChild(el("dt", { text: k }));
    dl.appendChild(el("dd", { text: v }));
  };
  add("アバター名", a.manifest.avatarName);
  add("メッシュ", `${a.meshes.blocks.length} 個`);
  add("シェイプキー", `${totalShapes} 個`);
  add("頂点数", totalVerts.toLocaleString("ja-JP"));
  add("マテリアル", `${a.materials.materials.length} 個`);
  if (a.manifest.unityVersion) add("Unity", a.manifest.unityVersion);
  if (a.manifest.toolVersion) add("エクスポータ", a.manifest.toolVersion);
  add("既存クリップ", `${a.face.existingClips.length} 個`);

  const warnBlock =
    a.warnings.length > 0
      ? el(
          "details",
          { class: "advanced" },
          el("summary", { text: `注意 ${a.warnings.length} 件` }),
          el("ul", { class: "warn-list" }, ...a.warnings.map((w) => el("li", { text: w }))),
        )
      : null;

  return el(
    "div",
    { class: "card" },
    el("h2", { text: "読み込み中のアバター", style: { marginTop: "0" } }),
    dl,
    warnBlock,
  );
}

function openAvatar(s: { id: string; name: string; savedAt: string }): void {
  void store
    .loadSaved(s.id)
    .then(() => {
      const savedAt = new Date(s.savedAt).toLocaleString("ja-JP");
      store.setBanner({
        kind: "info",
        title: `「${s.name}」を読み込みました（保存日時: ${savedAt}）`,
        detail: "",
        hint: null,
      });
    })
    .catch((e) => {
      const d = describeError(e);
      store.setBanner({ kind: "error", title: d.title, detail: d.detail, hint: d.hint });
    });
}

async function openAvatarMenu(s: { id: string; name: string }, anchor: HTMLElement): Promise<void> {
  const choice = await openMenu([{ label: "削除", value: "delete", danger: true }], anchor);
  if (choice === "delete") {
    const ok = await confirmDialog(
      "アバターを削除",
      `「${s.name}」と、そのクリップ・割り当てを削除します。元に戻せません。`,
      "削除する",
    );
    if (ok) await store.deleteSaved(s.id);
  }
}

function savedThumbnail(thumbnail: string | undefined): HTMLElement {
  return thumbnail
    ? el("img", { class: "saved-thumb", src: thumbnail, alt: "" })
    : el("div", { class: "saved-thumb placeholder" }, icon("avatar"));
}

function savedItemRowEl(s: { id: string; name: string; savedAt: string; thumbnail?: string }, nameText: string): HTMLElement {
  const isCurrent = store.avatar?.id === s.id;
  const menuBtn = el("button", { class: "icon-btn menu-trigger", text: "⋮", "aria-label": `${s.name} の操作` });
  menuBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    void openAvatarMenu(s, menuBtn);
  });
  return el(
    "div",
    {
      class: `clip-item${isCurrent ? "" : " clickable"}`,
      onclick: isCurrent ? undefined : () => openAvatar(s),
    },
    savedThumbnail(s.thumbnail),
    el("div", { class: "name", text: nameText }),
    el("span", { class: "count", text: isCurrent ? "表示中" : "" }),
    menuBtn,
  );
}

function savedItemRow(s: { id: string; name: string; savedAt: string; thumbnail?: string }): HTMLElement {
  return savedItemRowEl(s, new Date(s.savedAt).toLocaleString("ja-JP"));
}

function savedList(): HTMLElement | null {
  if (store.savedAvatars.length === 0) return null;

  // 同名でグルーピングし、複数バージョンあるものは折りたたんで表示する
  const groups = new Map<string, typeof store.savedAvatars>();
  for (const s of store.savedAvatars) {
    const arr = groups.get(s.name) ?? [];
    arr.push(s);
    groups.set(s.name, arr);
  }

  const items = [...groups.values()].map((versions) => {
    if (versions.length === 1) {
      const s = versions[0];
      return savedItemRowEl(s, s.name);
    }
    return el(
      "details",
      { class: "advanced" },
      el(
        "summary",
        {},
        el("span", { text: versions[0].name }),
        el("span", { class: "count", text: ` (${versions.length})` }),
      ),
      ...versions.map((s) => savedItemRow(s)),
    );
  });
  return el("div", { class: "section" }, el("h2", { text: "保存済みアバター" }), ...items);
}

/** 標準的な作業手順のヒントボックス（折りたたみ可能、初期は閉じておく）。 */
function workflowHintBox(): HTMLElement {
  return el(
    "details",
    { class: "advanced" },
    el("summary", { text: "使い方（標準的な手順）" }),
    el(
      "ol",
      { class: "warn-list" },
      el("li", { text: "エクスポート（Unityからwebに転送するためのファイル作成）" }),
      el("li", { text: "リレー転送（エクスポートで作ったファイルをwebに送る）" }),
      el("li", { text: "スマホもしくはPCブラウザで編集" }),
      el("li", { text: "スマホからUnityにリレー転送" }),
    ),
    el("p", {
      class: "muted",
      style: { marginLeft: "20px" },
      text: "4′. または、スマホで .fmapply を保存してPCに転送し、「適用」メニューから適用",
    }),
  );
}

function loadSection(): HTMLElement {
  return el(
    "div",
    { class: "section" },
    el("h2", { text: "アバターを読み込む" }),
    dropzone(),
    el("p", {
      class: "muted",
      text: "Unity の FaceMaker ウィンドウから書き出した .fmavatar ファイルを読み込みます。",
      style: { marginTop: "10px" },
    }),
    el("button", {
      class: "btn block",
      text: "6桁コードで受信（リレー）",
      onclick: () => openReceiveDialog(),
    }),
    el("p", {
      class: "muted",
      text: "Unity 側で「リレーで送信」したときに表示される「コード:鍵:IV」を貼り付けます。QR を読み取った URL でも構いません。",
    }),
    relaySettings(),
  );
}

export function renderAvatarView(): HTMLElement {
  const root = el("div", {});
  const hasSaved = store.savedAvatars.length > 0;

  root.appendChild(el("div", { class: "section" }, workflowHintBox()));

  const saved = savedList();
  if (hasSaved && saved) root.appendChild(saved);

  root.appendChild(
    hasSaved
      ? el("details", { class: "advanced" }, el("summary", { text: "+ 新しくアバターを読み込む" }), loadSection())
      : loadSection(),
  );

  if (!store.persistenceAvailable) {
    root.appendChild(
      el(
        "div",
        { class: "section" },
        el(
          "div",
          { class: "banner info" },
          el("h3", { text: "この環境では保存できません" }),
          el("p", {
            text: "ブラウザのストレージが使えないため、読み込んだアバターはページを閉じると消えます（プライベートブラウジング等）。",
          }),
        ),
      ),
    );
  }

  const card = currentAvatarCard();
  if (card) root.appendChild(el("div", { class: "section" }, card));

  if (!hasSaved && saved) root.appendChild(saved);

  return root;
}
